import { GENERIC_DNP_ID } from 'consts';
import { useAuthContext } from 'contexts/AuthContext';
import { useCallback } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { IDnsProvider, IResDnsProviders, IResNsServers } from 'types/api';
import { get } from './api';
import { API_ROUTES } from './apiRoutes';
import QueryKeys from './queryKeys';

const getDnsProviders = async (): Promise<IResDnsProviders> => {
	const res = await get<IResDnsProviders>(API_ROUTES.dnsProviders);
	return res.data;
};

export const useDnsProviders = <T = IResDnsProviders>(
	options?: UseQueryOptions<IResDnsProviders, unknown, T, QueryKeys[]>
) => {
	const { authRes } = useAuthContext();

	return useQuery({
		...(options || {}),
		queryKey: [QueryKeys.dnsProviders],
		queryFn: getDnsProviders,
		enabled: authRes.verified && (!options || options.enabled),
		staleTime: Infinity
	});
};

const getNsServers = async (domain: string) => {
	const res = await get<IResNsServers>(API_ROUTES.nsServers(domain));
	return res.data;
};

export const useNsServers = (domain: string | null) => {
	const { authRes } = useAuthContext();

	return useQuery({
		queryKey: [QueryKeys.dnsServers, domain],
		queryFn: () => getNsServers(domain!),
		enabled: authRes.verified && !!domain,
		staleTime: Infinity
	});
};

export const useDnsProviderByNsServers = (
	nsServersData: IResNsServers | undefined
) => {
	const select = useCallback(
		(data: IResDnsProviders): IDnsProvider | null => {
			const genericDnp = data.providers.find(
				(dnsProvider) => dnsProvider.id === GENERIC_DNP_ID
			)!;

			if (!nsServersData) {
				return null;
			}

			if (!nsServersData || !nsServersData.servers?.length) {
				return genericDnp;
			}

			const matchingProvider = data.providers.find((dnp) => {
				if (!dnp.nsPatterns?.length) {
					return false;
				}

				return dnp.nsPatterns.some((regexPattern) => {
					try {
						// eslint-disable-next-line security/detect-non-literal-regexp
						const regex = new RegExp(regexPattern, 'i');
						return nsServersData.servers
							?.map((nsServer) => nsServer.replace(/\.$/, ''))
							.some((nsServer) => regex.test(nsServer));
					} catch {
						return false;
					}
				});
			});

			return matchingProvider || genericDnp;
		},
		[nsServersData]
	);

	return useDnsProviders<IDnsProvider | null>({
		select,
		enabled: !!nsServersData
	});
};
